import {
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  UserCredential,
  User,
  onAuthStateChanged,
} from 'firebase/auth';
import { httpsCallable, HttpsCallableResult } from 'firebase/functions';
import { auth, functions } from './firebase';
import { STORAGE_KEYS } from '../utils/constants';
import { SignUpUserResponse } from '../types/firebase';
import { logger } from '../utils/logger';

/**
 * Sign up a new user with email and password
 * @param email User's email
 * @param password User's password
 * @returns Promise resolving to UserCredential
 */
export const signUp = async (email: string, password: string): Promise<HttpsCallableResult<SignUpUserResponse>> => {
  try {
    const signUpUser = httpsCallable<{ email: string; password: string }, SignUpUserResponse>(functions, 'signUpUser');
    return await signUpUser({ email, password });
  } catch (error) {
    logger.error('Error signing up', error as Error);
    throw error;
  }
};

/**
 * Sign in an existing user with email and password
 * @param email User's email
 * @param password User's password
 * @param rememberMe Whether to remember the user's credentials
 * @returns Promise resolving to UserCredential
 */
export const signIn = async (
  email: string,
  password: string,
  rememberMe: boolean
): Promise<UserCredential> => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);

    // Save only email if rememberMe is true (NEVER store passwords)
    if (rememberMe) {
      localStorage.setItem(STORAGE_KEYS.EMAIL, email);
      localStorage.setItem(STORAGE_KEYS.REMEMBER_ME, 'true');
    } else {
      localStorage.removeItem(STORAGE_KEYS.EMAIL);
      localStorage.removeItem(STORAGE_KEYS.REMEMBER_ME);
    }

    return userCredential;
  } catch (error) {
    logger.error('Error signing in', error as Error);
    throw error;
  }
};

/**
 * Sign out the current user
 * @returns Promise<void>
 */
export const logOut = async (): Promise<void> => {
  try {
    await signOut(auth);
  } catch (error) {
    logger.error('Error signing out', error as Error);
    throw error;
  }
};

/**
 * Send a password reset email
 * @param email User's email
 * @returns Promise<void>
 */
export const resetPassword = async (email: string): Promise<void> => {
  try {
    await sendPasswordResetEmail(auth, email);
  } catch (error) {
    logger.error('Error sending password reset email', error as Error);
    throw error;
  }
};

/**
 * Get the current user
 * @returns User | null
 */
export const getCurrentUser = (): User | null => {
  return auth.currentUser;
};

/**
 * Subscribe to auth state changes
 * @param callback Function to call when auth state changes
 * @returns Unsubscribe function
 */
export const subscribeToAuthChanges = (
  callback: (user: User | null) => void
): (() => void) => {
  return onAuthStateChanged(auth, callback);
};

/**
 * Get saved email from localStorage (password is never stored)
 * @returns Object containing email and rememberMe
 */
export const getSavedCredentials = (): {
  email: string;
  rememberMe: boolean;
} => {
  return {
    email: localStorage.getItem(STORAGE_KEYS.EMAIL) || '',
    rememberMe: localStorage.getItem(STORAGE_KEYS.REMEMBER_ME) === 'true',
  };
};

/**
 * Get API key from Firebase Functions or fallback to environment variable
 * @returns Promise resolving to API key
 */
export const getApiKey = async (): Promise<string> => {
  try {
    // Try to get API key from Firebase Functions first
    const getApiKeyFn = httpsCallable(functions, 'getApiKey');
    const result = await getApiKeyFn();
    return (result.data as { apiKey: string }).apiKey;
  } catch (error) {
    logger.debug('Error getting API key from Firebase, trying environment variable');

    // Fallback to environment variable for development
    const envApiKey = process.env.REACT_APP_OPENAI_API_KEY;
    if (envApiKey && envApiKey !== 'your_openai_api_key') {
      logger.debug('Using API key from environment variable');
      return envApiKey;
    }

    throw new Error('No API key available. Please set REACT_APP_OPENAI_API_KEY in your .env file or configure Firebase Functions.');
  }
};

/**
 * Fetch key spend info from Firebase Functions
 * @param apiKey Green Tokens API key
 * @returns Promise resolving to remaining balance
 */
export const fetchKeySpendInfo = async (apiKey: string): Promise<number> => {
  try {
    const fetchKeySpendInfoFn = httpsCallable(functions, 'fetchKeySpendInfo');
    const result = await fetchKeySpendInfoFn({ key: apiKey });

    // Validate response structure
    if (!result.data || typeof (result.data as any).remaining !== 'number') {
      logger.error('Invalid response from fetchKeySpendInfo', new Error(`Unexpected response: ${JSON.stringify(result.data)}`));
      throw new Error('Invalid response from server');
    }

    return (result.data as { remaining: number }).remaining;
  } catch (error: any) {
    // Log more detailed error information
    logger.error('Error fetching key spend info', error as Error);

    // Provide more context about the error
    if (error.code) {
      logger.error(`Firebase Functions error code: ${error.code}`);
    }
    if (error.message) {
      logger.error(`Error message: ${error.message}`);
    }
    if (error.details) {
      logger.error(`Error details: ${JSON.stringify(error.details)}`);
    }

    throw error;
  }
};
